// src/components/RefundPolicy.jsx
import React, { useMemo } from "react";

import NoticeBox from "./common/NoticeBox";

const RefundPolicy = () => {
  const header = useMemo(
    () => ({
      badge: "Policy Guide",
      title: "Refund Policy",
      description:
        "Our refund policy covers returns, exchanges, and refunds. We are committed to making the process simple and clear for every customer.",
      lastUpdated: "Last Updated: February 11, 2026",
    }),
    []
  );

  const policyData = useMemo(
    () => [
      {
        title: "1. Return Window",
        content: `We offer a 30-day return window from the date of delivery.

Items must be:

• Unused and in original condition
• In original packaging with all tags intact
• Returned with the original receipt or proof of purchase

Returns after 30 days will not be accepted unless required by law or for defective products under warranty.`,
      },
      {
        title: "2. Non-Returnable Items",
        content: `Certain items cannot be returned for health, safety, and hygiene reasons.

Non-returnable items include:

• Perishable goods such as food, flowers, and plants
• Personal care items such as cosmetics, underwear, and face masks
• Custom or personalized products
• Digital downloads and gift cards
• Clearance or final sale items
• Opened intimate or sanitary goods

If you receive a non-returnable item that is damaged or defective, please contact us immediately for assistance.`,
      },
      {
        title: "3. Return Process",
        content: `To return an item, please follow these steps:

1. Contact us at returns@yourcompany.com or 1-800-XXX-XXXX within 30 days
2. Receive a Return Authorization number within 2 business days
3. Package the item securely
4. Include the Return Authorization number with your package
5. Ship the item using a trackable shipping method
6. Refund will be processed after we receive and inspect the item

Please keep your tracking number until your refund is complete. We are not responsible for lost return packages.`,
      },
      {
        title: "4. Refund Methods and Timing",
        content: `Refunds are issued to your original payment method.

Estimated refund timing:

• Credit or debit cards: 7 to 10 business days, plus bank processing time
• PayPal: 5 to 7 business days
• Store credit: Issued immediately

Refunds include the product purchase price. Original shipping costs are non-refundable unless the return is due to our error.

If you have not received your refund after 15 days, please check with your bank first, then contact us at support@yourcompany.com.`,
      },
      {
        title: "5. Exchanges",
        content: `We accept exchanges for different sizes or colors within 30 days of delivery.

Exchange process:

1. Contact us to start the exchange
2. Receive an Exchange Authorization number
3. Return the original item
4. We will ship the exchange item once the original item is received

You are responsible for return shipping. We cover standard shipping for the exchange item.

Exchanges are subject to product availability. For faster service, you may place a new order and return the original item for a refund.`,
      },
      {
        title: "6. Damaged or Defective Items",
        content: `Please report damaged or defective items as soon as possible.

Reporting timeline:

• Shipping damage: Within 48 hours of delivery
• Product defects: Within 30 days of delivery

Please provide photos and your order number when contacting us.

For approved claims, we will provide a prepaid return label and offer a full refund or replacement.

Damage caused by misuse or normal wear is not covered.`,
      },
      {
        title: "7. Wrong Item Received",
        content: `If you receive the wrong item due to our error, please contact us within 7 days.

Please include photos of the item and your order number.

We will provide a prepaid return label and ship the correct item at no extra charge.

Please keep the wrong item in its original packaging and do not use or remove tags from it.`,
      },
      {
        title: "8. Refund for Cancelled Orders",
        content: `Orders cancelled before shipment will receive a full refund within 3 to 5 business days.

Orders cannot be cancelled after shipment. Once shipped, the standard return process applies.

We may cancel orders due to payment issues, suspected fraud, or product unavailability. If we cancel your order, a full refund will be issued.`,
      },
      {
        title: "9. International Returns",
        content: `International returns follow the same 30-day return policy.

Additional conditions:

• Customer pays return shipping
• Customs duties and taxes are non-refundable
• Items should be returned using trackable and insured shipping
• Refunds are issued in the original currency

Exchange rates may affect the final refunded amount.

For high-value international returns, please contact us before shipping the item back.`,
      },
      {
        title: "10. Lost or Stolen Packages",
        content: `If your package is lost in transit, please contact us after the estimated delivery date has passed by at least 5 days. We will help start a carrier claim.

If your package is marked as delivered but you cannot find it, please check with neighbors, your building, or your local carrier.

For stolen packages, contact us within 48 hours. A police report may be required.

To prevent delivery issues, we recommend using signature confirmation, secure delivery locations, or package lockers when available.`,
      },
      {
        title: "11. Restocking Fees",
        content: `Most returns do not have a restocking fee.

Restocking fees may apply to:

• Large items such as furniture or appliances: 20%
• Items returned without original packaging: 15%
• Custom or special orders: Up to 50%

No restocking fees apply for defective items or returns caused by our error.`,
      },
      {
        title: "12. Store Credit Option",
        content: `You may choose store credit instead of a refund.

Benefits of store credit:

• Instant processing
• 10% bonus on refund value
• No expiration
• Can be used across multiple orders

Example: A $100 refund can become $110 in store credit.`,
      },
      {
        title: "13. Holiday Return Extension",
        content: `Items purchased between November 1 and December 31 can be returned until January 31 of the following year.

Gift recipients may return items with a gift receipt for store credit.

All standard return conditions still apply.`,
      },
      {
        title: "14. Warranty and Extended Protection",
        content: `Manufacturer warranties vary by product and may range from 90 days to 2 years.

Optional extended protection plans may be available for electronics, appliances, and high-value items.

Extended protection plan examples:

• 1-Year Plan: $19.99 to $49.99
• 2-Year Plan: $39.99 to $99.99
• 3-Year Plan: $59.99 to $149.99`,
      },
      {
        title: "15. Final Sale Items",
        content: `Items marked as Final Sale or Clearance cannot be returned except in limited cases.

Exceptions may include:

• Item arrived defective
• Item arrived damaged
• Wrong item was shipped
• Return is required by state law

Final sale items will be clearly marked before purchase.`,
      },
      {
        title: "16. Contact Information",
        content: `Customer Service:

Email: returns@yourcompany.com
Phone: 1-800-XXX-XXXX
Hours: Monday to Friday, 9 AM to 6 PM EST
Saturday, 10 AM to 4 PM EST

Return Address:

Your Company Name - Returns Dept
Street Address
City, State, ZIP

Response Times:

Email: Within 24 hours
Phone: Immediate

Last Updated: February 11, 2026`,
      },
    ],
    []
  );

  const notice = useMemo(
    () => ({
      type: "warning",
      icon: "ph-warning",
      title: "Important Notice",
      message:
        "This refund policy is designed to ensure customer satisfaction while protecting our business. We reserve the right to refuse returns that do not meet our policy requirements or appear fraudulent.",
    }),
    []
  );

  return (
    <section className="refund-policy">
      <style>
        {`
          .refund-policy {
            padding: 40px 0;
            background: #fafafa;
          }

          .refund-policy .refund-wrapper {
            margin: 0 auto;
          }

          .refund-policy .refund-header {
            text-align: center;
            margin-bottom: 30px;
          }

          .refund-policy .refund-badge {
            display: inline-block;
            font-size: 13px;
            font-weight: 600;
            color: #0d6efd;
            background: #eef5ff;
            padding: 6px 14px;
            border-radius: 50px;
            margin-bottom: 12px;
          }

          .refund-policy .refund-title {
            font-size: 34px;
            line-height: 1.2;
            margin-bottom: 10px;
            color: #222;
          }

          .refund-policy .refund-description {
            max-width: 620px;
            margin: 0 auto 10px;
            font-size: 15px;
            line-height: 1.6;
            color: #666;
          }

          .refund-policy .last-updated {
            font-size: 13px;
            color: #888;
          }

          .refund-policy .refund-section {
            background: #fff;
            border: 1px solid #eee;
            border-radius: 12px;
            padding: 22px;
            margin-bottom: 16px;
          }

          .refund-policy .refund-section-title {
            font-size: 20px;
            line-height: 1.3;
            margin-bottom: 12px;
            color: #222;
            font-weight: 700;
          }

          .refund-policy .refund-section-text {
            font-size: 14px;
            line-height: 1.7;
            color: #666;
            margin-bottom: 0;
            white-space: pre-line;
          }

          .refund-policy .notice-small {
            margin-top: 24px;
          }

          @media (max-width: 575px) {
            .refund-policy {
              padding: 30px 0;
            }

            .refund-policy .refund-title {
              font-size: 28px;
            }

            .refund-policy .refund-section {
              padding: 18px;
            }

            .refund-policy .refund-section-title {
              font-size: 18px;
            }
          }
        `}
      </style>

      <div className="container">
        <div className="refund-wrapper">
          <div className="refund-header">
            <span className="refund-badge">{header.badge}</span>

            <h1 className="refund-title">{header.title}</h1>

            <p className="refund-description">{header.description}</p>

            <div className="last-updated">{header.lastUpdated}</div>
          </div>

          {policyData.map((section, index) => (
            <div key={index} className="refund-section">
              <h3 className="refund-section-title">{section.title}</h3>
              <p className="refund-section-text">{section.content}</p>
            </div>
          ))}

          <NoticeBox
            type={notice.type}
            icon={notice.icon}
            title={notice.title}
            message={notice.message}
            className="notice-small"
          />
        </div>
      </div>
    </section>
  );
};

export default RefundPolicy;